import { ApolloCache, NormalizedCacheObject, Reference } from "@apollo/client";

import { WatchlistListMyUserWatchlistsQuery } from "@/generated/graphql";

/**
 * Updates the cached `isWatchedByCurrentUser` field for a player based on watchlist membership.
 * @remarks
 * Modifies the Apollo cache to reflect whether a player is in any of the user's watchlists.
 * If no watchlist data is provided, marks the player as watched. Otherwise, checks all
 * watchlists to determine if the player is present.
 * @param cache - Apollo cache instance to modify
 * @param playerId - UUID of the player to update
 * @param listMyUserWatchlist - Optional query result containing user's watchlists
 */
export const changeStatusPlayerInWatchlist = async (
  cache: ApolloCache<NormalizedCacheObject>,
  playerId: string,
  listMyUserWatchlist?: WatchlistListMyUserWatchlistsQuery
) => {
  if (!listMyUserWatchlist) {
    cache.modify({
      id: cache.identify({ __typename: "Player", id: playerId }),
      fields: {
        isWatchedByCurrentUser() {
          return true;
        },
      },
    });
  } else {
    const listWatchlistPlayerIn =
      listMyUserWatchlist?.listMyUserWatchlists?.edges?.filter(
        _watchlist =>
          _watchlist?.node?.watchlist?.players?.edges?.findIndex(
            _player => _player.node.id === playerId
          ) !== -1
      );
    cache.modify({
      id: cache.identify({ __typename: "Player", id: playerId }),
      fields: {
        isWatchedByCurrentUser() {
          return !!listWatchlistPlayerIn.length;
        },
      },
    });
  }
};

/**
 * Player edge structure in Apollo cache.
 */
interface PlayerEdge {
  __typename: string;
  cursor: string;
  node: Reference;
}

/**
 * Adds or removes a player on the `Watchlist` entity's own `players` connection.
 * @remarks
 * SE-5408: the meta-fragment checkbox (`WatchlistAddRemoveButton`) reads
 * membership from `Watchlist.players.edges` (the `WatchlistWatchlistMeta`
 * fragment), but the rest of {@link addOrRemovePlayerInWatchlist} only updated
 * the `listWatchlistPlayers` ROOT_QUERY field. On the small-screen player-page
 * Add/Update-list sheet — where the `listWatchlistPlayers` query is never
 * cached — neither source reflected the toggle, so the checkbox only updated
 * after a page reload re-fetched `listMyUserWatchlists`. Updating the
 * normalized `Watchlist` entity makes the checkbox reflect membership
 * immediately on every surface (mobile + desktop) without a refetch.
 *
 * `cache.modify` is keyed by the field NAME (`players`), so it applies to every
 * stored variation of `Watchlist.players` (e.g. `players({"first":500})`)
 * regardless of pagination args.
 *
 * SE-5198: the count badge (Watchlist tab header + "All Lists" panel / pills
 * row) reads the scalar `Watchlist.playerCount`, which this same entity carries
 * but the toggle never touched — so the count stayed stale until a full reload.
 * We adjust it by ±1 alongside the `players` connection in the same
 * `cache.modify`, keyed off the caller's `type` (the checkbox/kebab toggle only
 * fires "add" for an absent player and "remove" for a present one, so the delta
 * always matches a real membership change). A per-op delta is intentionally used
 * over deriving from `players.edges.length`, since `playerCount` is the true
 * server total and `players` is capped at `first: 500`. Apollo skips the
 * modifier when `playerCount` isn't cached for the entity, so uncached surfaces
 * are unaffected.
 *
 * SE-6583: exported so the scouting surfaces (All Targets table row menu and
 * the Add-player-to-list modal) can wire it straight into their mutations'
 * `update` option. Those call sites only ran `refetchQueries`, and none of the
 * refetched queries select `playerCount` — so the badge stayed stale there
 * even after SE-5198. They deliberately use this helper rather than
 * {@link addOrRemovePlayerInWatchlist}: the `listWatchlistPlayers` ROOT_QUERY
 * rewrites in that wrapper are dead weight on surfaces that never cache it.
 * @param cache - Apollo cache instance to modify
 * @param watchlistId - UUID of the watchlist entity to update
 * @param playerId - UUID of the player to add or remove
 * @param type - Operation type: "add" or "remove"
 */
export const modifyWatchlistEntityPlayers = (
  cache: ApolloCache<NormalizedCacheObject>,
  watchlistId: string,
  playerId: string,
  type: "add" | "remove"
) => {
  const refPlayer = `Player:${playerId}`;
  cache.modify({
    id: cache.identify({ __typename: "Watchlist", id: watchlistId }),
    fields: {
      players(existing, { readField }) {
        const existingData = existing as { edges?: PlayerEdge[] };
        const edges = existingData?.edges ?? [];

        if (type === "remove") {
          return {
            ...existingData,
            edges: edges.filter(
              edge => readField("id", edge.node) !== playerId
            ),
          };
        }

        const alreadyPresent = edges.some(
          edge => readField("id", edge.node) === playerId
        );
        return alreadyPresent
          ? existingData
          : {
              ...existingData,
              edges: [
                ...edges,
                {
                  __typename: "PlayerEdge",
                  cursor: "",
                  node: { __ref: refPlayer },
                },
              ],
            };
      },
      playerCount(existing) {
        const current = typeof existing === "number" ? existing : 0;
        return type === "add" ? current + 1 : Math.max(0, current - 1);
      },
    },
  });
};

/**
 * Adds or removes a player from watchlist in Apollo cache.
 * @remarks
 * Updates multiple cached query variations for a watchlist to add or remove a player.
 * For "add" operations, only updates the base query. For "remove" operations, updates
 * all sorted variations to ensure consistency across different sort configurations.
 * Also updates the normalized `Watchlist` entity's own `players` connection so the
 * meta-fragment membership checkbox stays in sync without a refetch (SE-5408).
 * @param cache - Apollo cache instance to modify
 * @param watchlistId - UUID of the watchlist to update
 * @param playerId - UUID of the player to add or remove
 * @param type - Operation type: "add" to add player, "remove" to remove player
 */
export const addOrRemovePlayerInWatchlist = (
  cache: ApolloCache<NormalizedCacheObject>,
  watchlistId: string,
  playerId: string,
  type: "add" | "remove"
) => {
  const cacheFieldKeys = [
    `listWatchlistPlayers:{"watchlistId":"${watchlistId}"}`,
    `listWatchlistPlayers:{"watchlistId":"${watchlistId}","sortBy":"fitScore","sortOrder":"DESC"}`,
    `listWatchlistPlayers:{"watchlistId":"${watchlistId}","sortBy":"fitScore","sortOrder":"ASC"}`,
    `listWatchlistPlayers:{"watchlistId":"${watchlistId}","sortBy":"gpr","sortOrder":"DESC"}`,
    `listWatchlistPlayers:{"watchlistId":"${watchlistId}","sortBy":"gpr","sortOrder":"ASC"}`,
  ];
  const cacheId = cache.identify({ __typename: "Query", id: "ROOT_QUERY" });
  const refPlayer = `Player:${playerId}`;

  if (type === "add") {
    cache.modify({
      id: cacheId,
      fields: {
        [`listWatchlistPlayers:{"watchlistId":"${watchlistId}"}`](existing) {
          const existingData = existing as { edges?: PlayerEdge[] };
          const edges = existingData?.edges ?? [];
          const isHasPlayer = edges.find(edge => edge.node.__ref === refPlayer);
          return isHasPlayer
            ? existingData
            : {
                ...existingData,
                edges: [
                  ...edges,
                  {
                    __typename: "PlayerEdge",
                    cursor: "",
                    node: {
                      __ref: refPlayer,
                    },
                  },
                ],
              };
        },
      },
    });
  } else {
    cacheFieldKeys.forEach(fieldKey => {
      cache.modify({
        id: cacheId,
        fields: {
          [fieldKey]: existing => {
            const existingData = existing as { edges?: PlayerEdge[] };
            const edges = existingData?.edges ?? [];
            return {
              ...existingData,
              edges: edges.filter(edge => edge.node.__ref !== refPlayer),
            };
          },
        },
      });
    });
  }

  // SE-5408: keep the Watchlist entity's own players connection in sync — this
  // is the source the meta-fragment membership checkbox reads. Done last so the
  // existing listWatchlistPlayers ROOT_QUERY updates above are unaffected.
  modifyWatchlistEntityPlayers(cache, watchlistId, playerId, type);
};
